import {
  cashDrawerOpeningsResponseSchema,
  type CashDrawerOpening,
  type SessionIdentity,
} from "@cosmo/contracts";
import { type FormEvent, useEffect, useState } from "react";
import {
  loadCashDrawerConfiguration,
  openAuditedCashDrawer,
  saveCashDrawerConfiguration,
  type CashDrawerConfiguration,
} from "./cash-drawer";

const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("es-MX", {
    dateStyle: "medium",
    timeStyle: "medium",
  }).format(new Date(value));
}

function typeLabel(type: CashDrawerOpening["openingType"]): string {
  if (type === "ADMIN_TEST") return "Prueba administrativa";
  if (type === "COUNTER_SALE_CASH") return "Venta de mostrador";
  if (type === "OPTICAL_SALE_CASH") return "Venta graduada";
  return "Abono de trabajo";
}

function statusLabel(status: CashDrawerOpening["status"]): string {
  if (status === "OPENED") return "Abierta";
  if (status === "FAILED") return "Falló";
  return "Resultado pendiente";
}

async function responseMessage(response: Response): Promise<string> {
  try {
    const body = await response.json() as { error?: { message?: string } };
    return body.error?.message || "No fue posible consultar la bitácora.";
  } catch {
    return "No fue posible consultar la bitácora.";
  }
}

export function CashDrawerAdministration({
  session,
  onBack,
  onClose,
}: {
  session: SessionIdentity;
  onBack: () => void;
  onClose: () => void;
}) {
  const [configuration, setConfiguration] = useState<CashDrawerConfiguration>(() => loadCashDrawerConfiguration());
  const [reason, setReason] = useState("");
  const [openings, setOpenings] = useState<CashDrawerOpening[]>([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState("");

  async function loadOpenings() {
    const response = await fetch(`${apiBaseUrl}/api/v1/cash-drawer/openings`, {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error(await responseMessage(response));
    setOpenings(cashDrawerOpeningsResponseSchema.parse(await response.json()).data);
  }

  useEffect(() => {
    let active = true;
    loadOpenings()
      .catch((cause: unknown) => { if (active) setMessage(cause instanceof Error ? cause.message : "No fue posible consultar la bitácora."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  function saveConfiguration(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    saveCashDrawerConfiguration(configuration);
    setMessage("Configuración guardada en esta terminal.");
  }

  async function testDrawer() {
    if (reason.trim().length < 10) {
      setMessage("Escribe un motivo de al menos 10 caracteres para dejar trazabilidad.");
      return;
    }
    setTesting(true);
    setMessage("");
    saveCashDrawerConfiguration(configuration);
    const result = await openAuditedCashDrawer({
      openingType: "ADMIN_TEST",
      operationReference: null,
      paymentSequence: null,
      reason: reason.trim(),
    }, configuration);
    setMessage(result.message);
    if (result.opening) {
      setOpenings((current) => [
        result.opening!,
        ...current.filter((opening) => opening.id !== result.opening!.id),
      ]);
    }
    try {
      await loadOpenings();
    } catch {
      // El resultado inmediato permanece visible aunque falle la recarga del historial.
    }
    setTesting(false);
  }

  const currentBranch = session.branches.find((branch) => branch.id === session.branchId);

  return (
    <section className="admin-shell cash-drawer-admin" aria-labelledby="cash-drawer-title">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Administración · Hardware</p>
          <h1 id="cash-drawer-title">Caja e impresora Ethernet</h1>
          <p>Configura esta terminal, realiza una prueba controlada y consulta quién abrió la caja y a qué hora.</p>
        </div>
        <div className="step-actions heading-actions">
          <button className="secondary-button" onClick={onBack}>Volver a usuarios</button>
          <button className="secondary-button" onClick={onClose}>Volver al inicio</button>
        </div>
      </div>

      {message && <p className="form-message info-message" aria-live="polite">{message}</p>}

      <div className="admin-grid">
        <form className="user-editor" onSubmit={saveConfiguration}>
          <div className="editor-title"><div><h2>Configuración de esta terminal</h2><p>Se conserva únicamente en este equipo.</p></div></div>
          <div className="field-grid">
            <label>Nombre de terminal<input onChange={(event) => setConfiguration({ ...configuration, terminalLabel: event.target.value })} required value={configuration.terminalLabel} /></label>
            <label>Sucursal<input disabled value={currentBranch ? `${currentBranch.code} · ${currentBranch.name}` : "Sin sucursal"} /></label>
            <label>Dirección de la impresora<input inputMode="decimal" onChange={(event) => setConfiguration({ ...configuration, printerAddress: event.target.value })} required value={configuration.printerAddress} /></label>
            <label>Identificador Epson<input onChange={(event) => setConfiguration({ ...configuration, deviceId: event.target.value })} required value={configuration.deviceId} /></label>
            <label>Conector del cajón<select onChange={(event) => setConfiguration({ ...configuration, drawerPin: Number(event.target.value) as 2 | 5 })} value={configuration.drawerPin}><option value={2}>Pin 2 · Cajón 1</option><option value={5}>Pin 5 · Cajón 2</option></select></label>
          </div>
          <button className="primary-button">Guardar configuración</button>

          <aside className="drawer-test-panel">
            <div><strong>Prueba física sin venta</strong><p>No genera venta, pago, abono, factura ni movimiento contable. Sí registra administrador, sucursal, terminal, motivo, fecha y hora.</p></div>
            <label>Motivo de la prueba<textarea maxLength={500} minLength={10} onChange={(event) => setReason(event.target.value)} placeholder="Ej. Validación de instalación con cámaras activas" required value={reason} /></label>
            <button className="danger-button" disabled={testing || reason.trim().length < 10} onClick={testDrawer} type="button">{testing ? "Probando caja…" : "Abrir caja para prueba"}</button>
          </aside>
        </form>

        <div className="user-directory">
          <div className="directory-toolbar"><div><h2>Bitácora de aperturas</h2><p>Últimos 100 eventos de la sucursal, sin opción de editar o borrar.</p></div><button className="secondary-button compact-button" disabled={loading} onClick={() => { setLoading(true); loadOpenings().catch((cause: unknown) => setMessage(cause instanceof Error ? cause.message : "No fue posible actualizar.")).finally(() => setLoading(false)); }}>Actualizar</button></div>
          <div className="drawer-opening-list">
            {openings.map((opening) => (
              <article className="drawer-opening-row" key={opening.id}>
                <div><span className="eyebrow">{typeLabel(opening.openingType)}</span><h3>{opening.operationReference || "Sin venta asociada"}</h3><p>{opening.reason}</p></div>
                <div className="drawer-opening-meta">
                  <strong className={opening.status === "OPENED" ? "active-status" : opening.status === "FAILED" ? "error-text" : "protected-note"}>{statusLabel(opening.status)}</strong>
                  <span>{formatDate(opening.requestedAt)}</span>
                  <span>{opening.actorName}</span>
                  <span>{opening.terminalLabel} · {opening.printerAddress} · pin {opening.drawerPin}</span>
                  {opening.paymentSequence && <span>Abono {opening.paymentSequence}</span>}
                  {opening.errorMessage && <small>{opening.errorMessage}</small>}
                </div>
              </article>
            ))}
            {!loading && openings.length === 0 && <p className="empty-state">Todavía no hay aperturas registradas en esta sucursal.</p>}
            {loading && <p className="empty-state">Consultando bitácora…</p>}
          </div>
        </div>
      </div>
    </section>
  );
}
